import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { SocketContext } from '../contexts/SocketContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  IconButton,
  Avatar,
  useMediaQuery,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  ArrowBack
} from '@mui/icons-material';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import CatLoadingSpinner from './CatLoadingSpinner';
import LynaAvatar from './LynaAvatar';

registerLocale('es', es);

interface Board {
  id: string;
  type: string;
  name: string;
  description: string;
  business: { name: string };
  availabilitySettings: {
    allow247?: boolean;
    timeSlotDuration?: number;
    turnsPerHour?: number;
    weeklyAvailability?: {
      lunes: any[];
      martes: any[];
      miercoles: any[];
      jueves: any[];
      viernes: any[];
      sabado: any[];
      domingo: any[];
    };
    blockedDates?: Record<string, string>;
  };
  pricing: {
    basePrice?: number;
    perHour?: number;
    perDay?: number;
  };
  capacity: number;
}

interface AvailabilitySlot {
  startDate: Date;
  endDate: Date;
  bookedCount: number;
  capacity: number;
  availableSpots: number;
}

interface AvailabilityData {
  capacity: number;
  availability: AvailabilitySlot[];
}

type BookingStep = 'name' | 'date' | 'time' | 'contact' | 'success';

const STEPS = ['Tu nombre', 'Fecha', 'Horario', 'Contacto', 'Confirmar'];

const PublicReservationWidget: React.FC<{ boardId?: string }> = ({ boardId: propBoardId }) => {
  const { boardId: paramBoardId } = useParams<{ boardId: string }>();
  const boardId = propBoardId || paramBoardId;
  const socket = useContext(SocketContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const bg = isDark ? '#121212' : '#FAFAFA';
  const cardBg = isDark ? '#1E1E1E' : 'white';
  const textPrimary = isDark ? '#E0E0E0' : '#2D3436';
  const textSecondary = isDark ? '#B0B0B0' : '#666';
  const lynaBg = isDark ? '#2A1A20' : '#FFF0F5';
  const inputBg = isDark ? '#2A3A4A' : '#E3F2FD';
  const summaryBg = isDark ? '#1A2A1A' : 'white';
  const summaryBorder = '#FF6B9D';
  const dateIndicatorBg = isDark ? '#1A2A1A' : '#E8F5E9';
  const slotBg = isDark ? '#2A2A2A' : '#FFF';
  const slotDisabledBg = isDark ? '#1A1A1A' : '#F5F5F5';
  const helpBg = isDark ? '#2A1A20' : '#FFF0F5';
  const headerBg = isDark ? '#1A1A1A' : '#2D3436';
  const stepperBg = isDark ? '#1E1E1E' : 'white';
  const bottomBarBg = isDark ? '#1E1E1E' : 'white';
  const calendarDayColor = isDark ? '#E0E0E0' : '#2D3436';
  const calendarDisabledColor = isDark ? '#555' : '#CCC';
  const calendarHoverBg = isDark ? '#3A2030' : '#FFE0E0';
  const datePickerBg = isDark ? '#1E1E1E' : 'white';
  const datePickerBorder = isDark ? '#333' : 'transparent';

  const [board, setBoard] = useState<Board | null>(null);
  const [availabilityData, setAvailabilityData] = useState<AvailabilityData | null>(null);
  const [loading, setLoading] = useState(false);

  const [bookingStep, setBookingStep] = useState<BookingStep>('name');
  const [prevStep, setPrevStep] = useState<BookingStep>('name');
  const [transitionDirection, setTransitionDirection] = useState<'left' | 'right'>('left');
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');

  const [lynaMessage, setLynaMessage] = useState('¡Hola! 👋 Soy Lyna, tu asistente virtual. ¿Cómo te llamás?');
  const [lynaMood, setLynaMood] = useState<'happy' | 'thinking' | 'excited'>('happy');

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    phone: ''
  });

  const [countryPrefix, setCountryPrefix] = useState('+549');

  const countryOptions = [
    { code: '+549', flag: '🇦🇷', name: 'Argentina' },
    { code: '+54', flag: '🇦🇷', name: 'Arg (Sin 9)' },
    { code: '+598', flag: '🇺🇾', name: 'Uruguay' },
    { code: '+56', flag: '🇨🇱', name: 'Chile' },
    { code: '+57', flag: '🇨🇴', name: 'Colombia' },
    { code: '+51', flag: '🇵🇪', name: 'Perú' },
    { code: '+55', flag: '🇧🇷', name: 'Brasil' },
    { code: '+52', flag: '🇲🇽', name: 'México' },
    { code: '+53', flag: '🇨🇺', name: 'Cuba' },
    { code: '+58', flag: '🇻🇪', name: 'Venezuela' },
    { code: '+1', flag: '🇺🇸', name: 'EE.UU' },
    { code: '+1', flag: '🇨🇦', name: 'Canadá' },
    { code: '+44', flag: '🇬🇧', name: 'Reino Unido' },
    { code: '+49', flag: '🇩🇪', name: 'Alemania' },
    { code: '+33', flag: '🇫🇷', name: 'Francia' },
    { code: '+34', flag: '🇪🇸', name: 'España' },
    { code: '+39', flag: '🇮🇹', name: 'Italia' },
    { code: '+21', flag: '🇨🇳', name: 'China' },
    { code: '+81', flag: '🇯🇵', name: 'Japón' },
    { code: '+82', flag: '🇰🇷', name: 'Corea' },
    { code: '+61', flag: '🇦🇺', name: 'Australia' },
    { code: '+507', flag: '🇵🇦', name: 'Panamá' },
    { code: '+505', flag: '🇳🇮', name: 'Nicaragua' },
    { code: '+502', flag: '🇬🇹', name: 'Guatemala' },
    { code: '+503', flag: '🇸🇻', name: 'El Salvador' },
    { code: '+504', flag: '🇭🇳', name: 'Honduras' },
    { code: '+506', flag: '🇨🇷', name: 'Costa Rica' },
    { code: '+593', flag: '🇪🇨', name: 'Ecuador' },
    { code: '+595', flag: '🇵🇾', name: 'Paraguay' },
    { code: '+1', flag: '🇩🇴', name: 'RD' },
    { code: '+1', flag: '🇭🇹', name: 'Haití' },
    { code: '+1', flag: '🇵🇷', name: 'PR' },
    { code: '+212', flag: '🇲🇦', name: 'Marruecos' },
    { code: '+20', flag: '🇪🇬', name: 'Egipto' },
    { code: '+966', flag: '🇸🇦', name: 'Arabia' },
    { code: '+971', flag: '🇦🇪', name: 'UAE' },
    { code: '+972', flag: '🇮🇱', name: 'Israel' },
    { code: '+90', flag: '🇹🇷', name: 'Turquía' },
    { code: '+7', flag: '🇷🇺', name: 'Rusia' },
    { code: '+380', flag: '🇺🇦', name: 'Ucranía' },
    { code: '+48', flag: '🇵🇱', name: 'Polonia' },
    { code: '+31', flag: '🇳🇱', name: 'Holanda' },
    { code: '+32', flag: '🇧🇪', name: 'Bélgica' },
    { code: '+41', flag: '🇨🇭', name: 'Suiza' },
    { code: '+43', flag: '🇦🇹', name: 'Austria' },
    { code: '+45', flag: '🇩🇰', name: 'Dinamarca' },
    { code: '+46', flag: '🇸🇪', name: 'Suecia' },
    { code: '+47', flag: '🇳🇴', name: 'Noruega' },
    { code: '+358', flag: '🇫🇮', name: 'Finlandia' },
    { code: '+30', flag: '🇬🇷', name: 'Grecia' },
    { code: '+36', flag: '🇭🇺', name: 'Hungría' },
    { code: '+40', flag: '🇷🇴', name: 'Rumania' },
    { code: '+387', flag: '🇧🇦', name: 'Bosnia' },
    { code: '+381', flag: '🇷🇸', name: 'Serbia' },
    { code: '+385', flag: '🇭🇷', name: 'Croacia' },
    { code: '+386', flag: '🇸🇮', name: 'Eslovenia' },
    { code: '+420', flag: '🇨🇿', name: 'Rep.Checa' },
    { code: '+421', flag: '🇸🇰', name: 'Eslovaquia' },
    { code: '+373', flag: '🇲🇩', name: 'Moldavia' },
    { code: '+995', flag: '🇬🇪', name: 'Georgia' },
    { code: '+374', flag: '🇦🇲', name: 'Armenia' },
    { code: '+994', flag: '🇦🇿', name: 'Azerbaiyán' },
    { code: '+996', flag: '🇰🇬', name: 'Kirguistán' },
    { code: '+992', flag: '🇹🇯', name: 'Tayikistán' },
    { code: '+993', flag: '🇹🇲', name: 'Turkmenistán' },
    { code: '+998', flag: '🇺🇿', name: 'Uzbekistán' },
    { code: '+977', flag: '🇳🇵', name: 'Nepal' },
    { code: '+91', flag: '🇮🇳', name: 'India' },
    { code: '+92', flag: '🇵🇰', name: 'Pakistán' },
    { code: '+880', flag: '🇧🇩', name: 'Bangladesh' },
    { code: '+95', flag: '🇲🇲', name: 'Myanmar' },
    { code: '+855', flag: '🇰🇭', name: 'Camboya' },
    { code: '+856', flag: '🇱🇦', name: 'Laos' },
    { code: '+66', flag: '🇹🇭', name: 'Tailandia' },
    { code: '+60', flag: '🇲🇾', name: 'Malasia' },
    { code: '+65', flag: '🇸🇬', name: 'Singapur' },
    { code: '+62', flag: '🇮🇩', name: 'Indonesia' },
    { code: '+673', flag: '🇧🇳', name: 'Brunei' },
    { code: '+264', flag: '🇳🇦', name: 'Namibia' },
    { code: '+27', flag: '🇿🇦', name: 'Sudáfrica' },
    { code: '+254', flag: '🇰🇪', name: 'Kenia' },
    { code: '+255', flag: '🇹🇿', name: 'Tanzania' },
    { code: '+256', flag: '🇺🇬', name: 'Uganda' },
    { code: '+250', flag: '🇷🇼', name: 'Ruanda' },
    { code: '+243', flag: '🇨🇩', name: 'Congo' },
    { code: '+244', flag: '🇦🇴', name: 'Angola' },
    { code: '+237', flag: '🇨🇲', name: 'Camerún' },
    { code: '+225', flag: '🇨🇮', name: 'Costa Marfil' },
    { code: '+229', flag: '🇧🇯', name: 'Benin' },
    { code: '+241', flag: '🇬🇦', name: 'Gabón' },
    { code: '+240', flag: '🇬🇶', name: 'Guinea Ec.' },
    { code: '+245', flag: '🇬🇳', name: 'Guinea' },
    { code: '+222', flag: '🇲🇷', name: 'Mauritania' },
    { code: '+249', flag: '🇸🇩', name: 'Sudán' },
    { code: '+251', flag: '🇪🇹', name: 'Etiopía' },
  ];

  useEffect(() => {
    if (boardId) {
      fetchBoardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  useEffect(() => {
    if (!socket || !boardId) return;
    socket.emit('joinBoard', boardId);
    const handleUpdate = () => fetchAvailability();
    socket.on('reservationCreated', handleUpdate);
    socket.on('reservationUpdated', handleUpdate);
    socket.on('reservationDeleted', handleUpdate);
    socket.on('reservationCancelled', handleUpdate);
    socket.on('reservationRestored', handleUpdate);
    const intervalId = setInterval(fetchAvailability, 30000);
    return () => {
      socket.off('reservationCreated', handleUpdate);
      socket.off('reservationUpdated', handleUpdate);
      socket.off('reservationDeleted', handleUpdate);
      socket.off('reservationCancelled', handleUpdate);
      socket.off('reservationRestored', handleUpdate);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, boardId]);

  useEffect(() => {
    const handlePopState = () => {
      handleBack();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingStep]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dateParam = params.get('date');
    const timeParam = params.get('time');
    if (dateParam) {
      const date = new Date(dateParam);
      if (!isNaN(date.getTime())) {
        setStartDate(date);
        if (timeParam) {
          setSelectedTimeSlot(timeParam);
        }
      }
    }
  }, []);

  const fetchAvailability = async () => {
    try {
      const availRes = await axios.get(`/api/boards/${boardId}/availability`);
      setAvailabilityData({
        ...availRes.data,
        availability: availRes.data.availability.map((slot: any) => ({
          ...slot,
          startDate: new Date(slot.startDate),
          endDate: new Date(slot.endDate)
        }))
      });
    } catch (err) {
      console.error('Error refreshing availability:', err);
    }
  };

  const fetchBoardData = async () => {
    setLoading(true);
    setLynaMood('thinking');
    setLynaMessage('Cargando tu reserva... 🐱');
    try {
      const [boardRes, availRes] = await Promise.all([
        axios.get(`/api/boards/${boardId}`),
        axios.get(`/api/boards/${boardId}/availability`)
      ]);
      setBoard(boardRes.data);
      setAvailabilityData({
        ...availRes.data,
        availability: availRes.data.availability.map((slot: any) => ({
          ...slot,
          startDate: new Date(slot.startDate),
          endDate: new Date(slot.endDate)
        }))
      });
      setLynaMood('happy');
      setLynaMessage(`¡Bienvenido a ${boardRes.data.name}! Elige una fecha para tu reserva. 📅`);
    } catch (err) {
      setError('No pudimos cargar la información.');
      setLynaMessage('Ups, tuve problemas para conectar. 😿');
      setLynaMood('thinking');
    } finally {
      setLoading(false);
    }
  };

  const isDateRange = ['rooms', 'cabins', 'camping'].includes(board?.type || '');

  const getExcludedDates = () => {
    const excluded: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thirtyDaysLater = new Date(today);
    thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 90);

    const weeklyAvail = board?.availabilitySettings?.weeklyAvailability;
    const allow247 = board?.availabilitySettings?.allow247;
    const blockedDates = board?.availabilitySettings?.blockedDates || {};
    const dayNamesMap = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];

    for (let d = new Date(today); d <= thirtyDaysLater; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const dayName = dayNamesMap[d.getDay()];

      if (blockedDates[dateStr]) {
        excluded.push(new Date(d));
        continue;
      }

      let isDayAvailable = false;
      if (allow247) {
        isDayAvailable = true;
      } else if (weeklyAvail && weeklyAvail[dayName as keyof typeof weeklyAvail]?.length > 0) {
        isDayAvailable = true;
      }

      if (!isDayAvailable) {
        excluded.push(new Date(d));
      }
    }
    return excluded;
  };

  const generateTimeSlots = () => {
    if (!startDate) return [];

    const dateStr = startDate.toISOString().split('T')[0];
    const blockedDates = board?.availabilitySettings?.blockedDates || {};
    if (blockedDates[dateStr]) return [];

    const dayNamesMap = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
    const dayName = dayNamesMap[startDate.getDay()];
    const weeklyAvail = board?.availabilitySettings?.weeklyAvailability;
    const allow247 = board?.availabilitySettings?.allow247;
    const timeSlotDuration = board?.availabilitySettings?.timeSlotDuration || 60;
    const turnsPerHour = board?.availabilitySettings?.turnsPerHour || 1;

    if (!allow247 && weeklyAvail) {
      const daySlots = weeklyAvail[dayName as keyof typeof weeklyAvail];
      if (!daySlots || daySlots.length === 0) return [];
    }

    let startHour = 9, endHour = 18;
    if (allow247) {
      startHour = 0;
      endHour = 24;
    } else if (weeklyAvail && weeklyAvail[dayName as keyof typeof weeklyAvail]?.length > 0) {
      const daySlots = weeklyAvail[dayName as keyof typeof weeklyAvail];
      if (daySlots[0]?.start) startHour = parseInt(daySlots[0].start.split(':')[0]);
      if (daySlots[daySlots.length - 1]?.end) endHour = parseInt(daySlots[daySlots.length - 1].end.split(':')[0]);
    }

    const slots = [];
    const slotsPerHour = Math.floor(60 / timeSlotDuration);

    for (let h = startHour; h < endHour; h++) {
      for (let slotIdx = 0; slotIdx < slotsPerHour; slotIdx++) {
        const minutes = slotIdx * timeSlotDuration;
        const timeStr = `${h.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        const checkDate = new Date(startDate);
        checkDate.setHours(h, minutes, 0, 0);

        const slotInfo = availabilityData?.availability.find(s =>
          s.startDate.getTime() === checkDate.getTime()
        );

        const bookedCount = slotInfo?.bookedCount || 0;
        const capacity = turnsPerHour;
        const availableSpots = capacity - bookedCount;
        const available = availableSpots > 0;

        slots.push({ time: timeStr, available, bookedCount, capacity, availableSpots });
      }
    }
    return slots;
  };

  const handleDateChange = (date: Date | null) => {
    setStartDate(date);
    setEndDate(null);
    setSelectedTimeSlot(null);
    if (date) {
      setLynaMessage(`${userName}, ¡qué buena fecha! Ahora elegí el horario que más te convenga. 🕐`);
      setLynaMood('happy');
      setPrevStep(bookingStep);
      setTransitionDirection('left');
      setBookingStep('time');
    }
  };

  const handleNameSubmit = () => {
    if (userName.trim().length < 2) {
      setError('¿Cómo te llamás? Escribí tu nombre por favor.');
      return;
    }
    setError('');
    setLynaMessage(`¡Mucho gusto, ${userName}! 👋 Ahora elegí la fecha de tu reserva. 📅`);
    setLynaMood('happy');
    setPrevStep(bookingStep);
    setTransitionDirection('left');
    setBookingStep('date');
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTimeSlot(time);
    setLynaMessage(`${userName}, ¡perfecto! Ahora necesito tu email y teléfono para confirmar. 📱`);
    setLynaMood('excited');
    setPrevStep(bookingStep);
    setTransitionDirection('left');
    setBookingStep('contact');
  };

  const handleBack = useCallback(() => {
    if (bookingStep === 'name') {
      setBookingStep('name');
    } else if (bookingStep === 'date') {
      setPrevStep(bookingStep);
      setTransitionDirection('right');
      setBookingStep('name');
      setLynaMessage(`¡Hola ${userName}! 👋 Elegí la fecha de tu reserva. 📅`);
    } else if (bookingStep === 'time') {
      setPrevStep(bookingStep);
      setTransitionDirection('right');
      setBookingStep('date');
      setLynaMessage(`${userName}, ¿qué fecha te sirve? 😊`);
      setLynaMood('happy');
    } else if (bookingStep === 'contact') {
      setPrevStep(bookingStep);
      setTransitionDirection('right');
      setBookingStep('time');
      setSelectedTimeSlot(null);
      setLynaMessage(`${userName}, elegí el mejor horario para vos. 🕐`);
      setLynaMood('happy');
    }
  }, [bookingStep, userName]);

  const handleBook = async () => {
    setLoading(true);
    setLynaMessage('Confirmando tu reserva... 🎉');
    setLynaMood('thinking');

    try {
      if (!formData.email || !formData.phone || formData.phone.length < 8) {
        setError('Completá tu email y teléfono, por favor.');
        setLynaMessage(`${userName}, falta un dato, ¿me ayudás? 😊`);
        setLoading(false);
        return;
      }

      const fullPhone = countryPrefix + formData.phone;
      const payload: any = {
        customerName: userName,
        customerEmail: formData.email,
        customerPhone: fullPhone,
      };

      if (isDateRange) {
        if (!startDate || !endDate) return;
        payload.startDate = startDate.toISOString();
        payload.endDate = endDate.toISOString();
      } else {
        if (!startDate || !selectedTimeSlot) return;
        const start = new Date(startDate);
        const [h, m] = selectedTimeSlot.split(':');
        start.setHours(Number(h), Number(m), 0, 0);
        payload.startDate = start.toISOString();
        const timeSlotDuration = board?.availabilitySettings?.timeSlotDuration || 60;
        payload.endDate = new Date(start.getTime() + timeSlotDuration * 60 * 1000).toISOString();
      }

      await axios.post(`/api/boards/${boardId}/reservations`, payload);
      setLynaMessage('¡Listo! Tu reserva está confirmada. ¡Te esperamos! 😻');
      setLynaMood('excited');
      setPrevStep(bookingStep);
      setTransitionDirection('left');
      setBookingStep('success');
    } catch (err) {
      setError('Hubo un error. Por favor intenta de nuevo.');
      setLynaMessage('Algo salió mal. ¡Inténtalo de nuevo! 😿');
      setLynaMood('thinking');
    } finally {
      setLoading(false);
    }
  };

  const getLynaEmoji = () => {
    switch (lynaMood) {
      case 'happy': return 'happy';
      case 'excited': return 'success';
      case 'thinking': return 'tip';
      default: return 'tip';
    }
  };

  const getStepIndex = () => {
    switch (bookingStep) {
      case 'name': return 0;
      case 'date': return 1;
      case 'time': return 2;
      case 'contact': return 3;
      case 'success': return 4;
    }
  };

  const calendarStyles = {
    '& .react-datepicker': {
      fontSize: '1.1rem',
      border: `1px solid ${datePickerBorder}`,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      borderRadius: 3,
      color: calendarDayColor,
      backgroundColor: datePickerBg,
    },
    '& .react-datepicker__header': {
      bgcolor: '#FF6B9D',
      color: 'white',
      py: 2,
      borderBottom: 'none',
    },
    '& .react-datepicker__current-month': {
      color: 'white !important',
      fontSize: '1.3rem',
      fontWeight: 'bold',
    },
    '& .react-datepicker__day-name': {
      color: 'white !important',
      fontWeight: 'bold',
      width: 45,
      lineHeight: '2.5rem',
      fontSize: '1rem',
    },
    '& .react-datepicker__day': {
      width: 45,
      lineHeight: '2.5rem',
      fontSize: '1.2rem',
      fontWeight: '500',
      borderRadius: '50%',
      margin: '2px',
      color: calendarDayColor,
      '&:hover': {
        bgcolor: calendarHoverBg,
      },
    },
    '& .react-datepicker__day--selected': {
      bgcolor: '#FF6B9D !important',
      color: 'white !important',
      fontWeight: 'bold',
      fontSize: '1.3rem',
      boxShadow: '0 2px 8px rgba(255, 107, 157, 0.4)',
    },
    '& .react-datepicker__day--keyboard-selected': {
      bgcolor: '#FF8E53',
      color: 'white !important',
    },
    '& .react-datepicker__day--disabled': {
      color: `${calendarDisabledColor} !important`,
      cursor: 'not-allowed',
    },
    '& .react-datepicker__navigation': {
      top: 20,
    },
    '& .react-datepicker__navigation-icon::before': {
      borderColor: 'white',
    },
  };

  // Full screen name step
  const renderNameStep = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: bg,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ bgcolor: '#FF6B9D', color: 'white', py: 4, px: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <LynaAvatar size={100} isCelebrating={lynaMood === 'excited'} />
        <Typography variant="h5" fontWeight="bold" color="white" sx={{ mt: 1 }}>¡Hola! 👋</Typography>
      </Box>

      <Box sx={{ p: 3, bgcolor: lynaBg, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: 3, flex: 1, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
          <Typography variant="body1" fontWeight="500" color={textPrimary}>
            Soy Lyna, tu asistente virtual. ¿Cómo te llamás?
          </Typography>
        </Paper>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 4, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
          <TextField
            fullWidth
            autoFocus
            label="Tu nombre"
            value={userName}
            onChange={e => setUserName(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleNameSubmit()}
            error={!!error}
            helperText={error || 'Ej: María, Juan, Pedro'}
            sx={{ mb: 3 }}
            inputProps={{
              style: {
                fontSize: '1.5rem',
                textAlign: 'center',
                padding: '16px',
                color: textPrimary
              }
            }}
            InputLabelProps={{ style: { fontSize: '1.1rem', color: textSecondary } }}
          />
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleNameSubmit}
            disabled={!userName.trim() || userName.trim().length < 2}
            sx={{
              py: 2,
              borderRadius: 3,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #FF6B9D 30%, #FF8E53 90%)',
            }}
          >
            Continuar ▶
          </Button>
        </Paper>
      </Box>
    </Box>
  );

  // Full screen calendar
  const renderDateStep = () => (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: bg,
      zIndex: 1300,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ bgcolor: '#FF6B9D', color: 'white', py: 3, px: 2, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" color="white">📅 Elegí tu fecha</Typography>
      </Box>

      <Box sx={{ p: 2, bgcolor: lynaBg, display: 'flex', alignItems: 'center', gap: 2 }}>
        <LynaAvatar size={48} />
        <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, flex: 1, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
          <Typography variant="body2" color={textPrimary}>{lynaMessage}</Typography>
        </Paper>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', pt: 2 }}>
        <Box sx={calendarStyles}>
          <DatePicker
            locale="es"
            inline
            selected={startDate}
            onChange={handleDateChange}
            minDate={new Date()}
            excludeDates={getExcludedDates()}
            dateFormat="dd 'de' MMMM"
            showDisabledMonthNavigation
          />
        </Box>
      </Box>

      <Box sx={{ p: 2, textAlign: 'center', bgcolor: helpBg }}>
        <Typography variant="body2" color={textSecondary} fontWeight="500">
          📅 Los días en gris no están disponibles
        </Typography>
      </Box>
    </Box>
  );

  // Full screen time selection
  const renderTimeStep = () => {
    const slots = generateTimeSlots();
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: bg,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ bgcolor: headerBg, color: 'white', py: 2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton onClick={handleBack} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" color="white" sx={{ flex: 1, textAlign: 'center' }}>
            🕐 Elige un horario
          </Typography>
          <Box sx={{ width: 48 }} />
        </Box>

        <Box sx={{ p: 2, bgcolor: lynaBg, display: 'flex', alignItems: 'center', gap: 2 }}>
          <LynaAvatar size={48} isCelebrating={lynaMood === 'excited'} />
          <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, flex: 1, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
            <Typography variant="body2" color={textPrimary}>{lynaMessage}</Typography>
          </Paper>
        </Box>

        <Box sx={{ p: 2, bgcolor: dateIndicatorBg, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CalendarToday fontSize="small" sx={{ color: '#4CAF50' }} />
          <Typography variant="body1" fontWeight="bold" color="#4CAF50">
            {startDate?.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {slots.length > 0 ? (
            <Grid container spacing={2}>
              {slots.map((slot) => (
                <Grid size={6} key={slot.time}>
                  <Button
                    variant={selectedTimeSlot === slot.time ? 'contained' : 'outlined'}
                    fullWidth
                    disabled={!slot.available}
                    onClick={() => handleTimeSelect(slot.time)}
                    sx={{
                      py: 2.5,
                      borderRadius: 3,
                      bgcolor: selectedTimeSlot === slot.time ? '#FF6B9D' : slot.available ? slotBg : slotDisabledBg,
                      color: selectedTimeSlot === slot.time ? 'white' : slot.available ? textPrimary : '#BBB',
                      borderColor: slot.available ? (isDark ? '#444' : '#E0E0E0') : 'transparent',
                      boxShadow: selectedTimeSlot === slot.time ? 3 : 0,
                      '&:active': { transform: 'scale(0.98)' }
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold" color="inherit">
                        {slot.time}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: selectedTimeSlot === slot.time ? 'white' : slot.available ? textSecondary : '#BBB' }}>
                        {slot.available
                          ? (slot.capacity === 1 ? '✅ Disponible' : `✅ ${slot.availableSpots} lugares`)
                          : '❌ Occupado'}
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color={textPrimary} gutterBottom fontWeight="bold">
                No hay horarios disponibles
              </Typography>
              <Typography variant="body2" color={textSecondary} sx={{ mb: 3 }}>
                Probá con otra fecha
              </Typography>
              <Button
                variant="outlined"
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ borderRadius: 3, color: '#FF6B9D', borderColor: '#FF6B9D' }}
              >
                Cambiar fecha
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Full screen contact form
  const renderContactStep = () => (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: bg,
      zIndex: 1300,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ bgcolor: headerBg, color: 'white', py: 2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <IconButton onClick={handleBack} sx={{ color: 'white' }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight="bold" color="white" sx={{ flex: 1, textAlign: 'center' }}>
          📱 Tu contacto
        </Typography>
        <Box sx={{ width: 48 }} />
      </Box>

      <Box sx={{ p: 2, bgcolor: lynaBg, display: 'flex', alignItems: 'center', gap: 2 }}>
        <LynaAvatar size={48} />
        <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, flex: 1, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
          <Typography variant="body2" color={textPrimary}>{lynaMessage}</Typography>
        </Paper>
      </Box>

      <Box sx={{ mx: 2, mt: 2, p: 2.5, bgcolor: summaryBg, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.08)', border: `2px solid ${summaryBorder}` }}>
        <Box sx={{ fontSize: '28px' }}>📋</Box>
        <Box>
          <Typography variant="body2" color="#FF6B9D" fontWeight="600" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Reserva para:</Typography>
          <Typography variant="body1" fontWeight="bold" color={textPrimary}>
            {startDate?.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="#FF6B9D">
            🕐 a las {selectedTimeSlot} hs
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
          <TextField
            fullWidth
            label="Tu email"
            margin="normal"
            type="email"
            value={formData.email}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            sx={{ mb: 3 }}
            inputProps={{ style: { fontSize: '1.2rem', padding: '14px', color: textPrimary } }}
            InputLabelProps={{ style: { fontSize: '1.1rem', color: textSecondary } }}
          />

          <Typography variant="body2" color={textPrimary} fontWeight="500" sx={{ mb: 1, mt: 2 }}>
            Teléfono (WhatsApp):
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <FormControl sx={{ minWidth: 90 }}>
              <Select
                value={countryPrefix}
                onChange={e => setCountryPrefix(e.target.value as string)}
                sx={{ 
                  bgcolor: inputBg,
                  fontSize: '1rem',
                  color: textPrimary,
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: textPrimary
                  }
                }}
              >
                {countryOptions.map(opt => (
                  <MenuItem key={opt.code} value={opt.code} sx={{ bgcolor: cardBg }}>
                    {opt.flag} {opt.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              value={formData.phone}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '');
                setFormData({ ...formData, phone: val });
              }}
              placeholder="11 1234 5678"
              inputProps={{ 
                style: { 
                  fontSize: '1.2rem', 
                  padding: '14px',
                  backgroundColor: inputBg,
                  color: textPrimary
                } 
              }}
            />
          </Box>
          <Typography variant="caption" color={textSecondary} sx={{ display: 'block', mb: 2 }}>
            Sin 0 ni 15 • Ej: 11 1234 5678
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Box>

      <Box sx={{ p: 3, bgcolor: bottomBarBg, boxShadow: isDark ? '0 -4px 20px rgba(0,0,0,0.3)' : '0 -4px 20px rgba(0,0,0,0.1)' }}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleBook}
          disabled={loading || !formData.email || !formData.phone || formData.phone.length < 8}
          sx={{
            py: 2,
            borderRadius: 3,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #FF6B9D 30%, #FF8E53 90%)',
            boxShadow: '0 4px 15px rgba(255, 107, 157, 0.4)',
            '&:hover': { transform: 'translateY(-2px)' }
          }}
        >
          {loading ? (
            <CircularProgress size={28} color="inherit" />
          ) : (
            <>🎉 Confirmar Reserva</>
          )}
        </Button>
      </Box>
    </Box>
  );

  // Full screen success
  const renderSuccessStep = () => (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: bg,
      zIndex: 1300,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3
    }}>
      <Box sx={{
        fontSize: '100px',
        animation: 'bounce 1s ease-in-out',
        '@keyframes bounce': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-20px)' }
        }
      }}>
        🎉
      </Box>

      <Typography variant="h4" fontWeight="bold" sx={{ mt: 2, mb: 1, textAlign: 'center' }} color={textPrimary}>
        ¡Listo, {userName}! 🎉
      </Typography>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mt: 2, width: '100%', maxWidth: 400, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CalendarToday sx={{ color: '#FF6B9D' }} />
          <Typography variant="body1" color={textPrimary}>
            {startDate?.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccessTime sx={{ color: '#FF6B9D' }} />
          <Typography variant="body1" color={textPrimary}>
            a las {selectedTimeSlot} hs
          </Typography>
        </Box>
      </Paper>

      <Typography variant="body2" color={textSecondary} sx={{ mt: 3, textAlign: 'center' }}>
        Te contactaremos a la brevedad para confirmar tu reserva. 📱
      </Typography>

      <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <LynaAvatar size={56} />
        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, bgcolor: lynaBg, border: isDark ? '1px solid #333' : 'none' }}>
          <Typography variant="body2" fontWeight="bold" color={textPrimary}>
            ¡Nos vemos pronto! 😺
          </Typography>
        </Paper>
      </Box>

      <Button
        variant="outlined"
        fullWidth
        onClick={() => window.location.reload()}
        sx={{ mt: 4, py: 1.5, borderRadius: 3, color: '#FF6B9D', borderColor: '#FF6B9D' }}
      >
        Hacer otra reserva
      </Button>
    </Box>
  );

  // Render based on step
  if (bookingStep === 'name' && isMobile) return renderNameStep();
  if (bookingStep === 'date' && isMobile) return renderDateStep();
  if (bookingStep === 'time' && isMobile) return renderTimeStep();
  if (bookingStep === 'contact' && isMobile) return renderContactStep();
  if (bookingStep === 'success' && isMobile) return renderSuccessStep();

  // Desktop view
  if (loading && !board) {
    return (
      <Box minHeight="100vh" display="flex" justifyContent="center" alignItems="center" bgcolor={bg}>
        <CatLoadingSpinner message="Preparando todo..." />
      </Box>
    );
  }

  if (!board) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: bg }}>
      <Box sx={{ bgcolor: headerBg, color: 'white', py: 3, px: 2, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" color="white">{board.name}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }} color="rgba(255,255,255,0.8)">por {board.business.name}</Typography>
      </Box>

      <Box sx={{ bgcolor: stepperBg, py: 2, px: 2 }}>
        <Stepper activeStep={getStepIndex()} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {bookingStep !== 'name' && (
        <Box sx={{ px: 2, py: 1 }}>
          <Button startIcon={<ArrowBack />} onClick={handleBack} color="inherit" sx={{ color: textPrimary }}>
            Volver
          </Button>
        </Box>
      )}

      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <LynaAvatar size={56} />
          <Paper elevation={2} sx={{ p: 2, borderRadius: 3, flex: 1, bgcolor: lynaBg, border: isDark ? '1px solid #333' : 'none' }}>
            <Typography variant="body1" color={textPrimary}>{lynaMessage}</Typography>
          </Paper>
        </Box>

        {bookingStep === 'name' && (
          <Fade in>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
              <Typography variant="h6" gutterBottom color={textPrimary}>👋 ¿Cómo te llamás?</Typography>
              <TextField
                fullWidth
                autoFocus
                label="Tu nombre"
                value={userName}
                onChange={e => setUserName(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleNameSubmit()}
                error={!!error}
                helperText={error || 'Ej: María, Juan, Pedro'}
                sx={{ mb: 3 }}
                inputProps={{ style: { color: textPrimary } }}
                InputLabelProps={{ style: { color: textSecondary } }}
              />
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleNameSubmit}
                disabled={!userName.trim() || userName.trim().length < 2}
                sx={{ py: 2, borderRadius: 3, background: 'linear-gradient(45deg, #FF6B9D 30%, #FF8E53 90%)', fontWeight: 'bold' }}
              >
                Continuar ▶
              </Button>
            </Paper>
          </Fade>
        )}

        {bookingStep === 'date' && (
          <Fade in>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
              <Typography variant="h6" gutterBottom color={textPrimary}>📅 Elegí tu fecha</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={calendarStyles}>
                  <DatePicker
                    locale="es"
                    inline
                    selected={startDate}
                    onChange={handleDateChange}
                    minDate={new Date()}
                    excludeDates={getExcludedDates()}
                    dateFormat="dd 'de' MMMM"
                  />
                </Box>
              </Box>
            </Paper>
          </Fade>
        )}

        {bookingStep === 'time' && (
          <Fade in>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
              <Typography variant="h6" gutterBottom color={textPrimary}>
                🕐 Elige un horario
              </Typography>
              <Box sx={{ p: 2, bgcolor: dateIndicatorBg, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
                <CalendarToday sx={{ color: '#4CAF50' }} />
                <Typography variant="body1" fontWeight="bold" color="#4CAF50">
                  {startDate?.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {generateTimeSlots().length > 0 ? (
                  generateTimeSlots().map((slot) => (
                    <Grid size={4} key={slot.time}>
                      <Button
                        variant={selectedTimeSlot === slot.time ? 'contained' : 'outlined'}
                        fullWidth
                        disabled={!slot.available}
                        onClick={() => handleTimeSelect(slot.time)}
                        sx={{
                          py: 2.5,
                          borderRadius: 3,
                          bgcolor: selectedTimeSlot === slot.time ? '#FF6B9D' : slot.available ? slotBg : slotDisabledBg,
                          color: selectedTimeSlot === slot.time ? 'white' : slot.available ? textPrimary : '#BBB',
                          borderColor: slot.available ? (isDark ? '#444' : '#E0E0E0') : 'transparent',
                          boxShadow: selectedTimeSlot === slot.time ? 3 : 0,
                          '&:active': { transform: 'scale(0.98)' }
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" fontWeight="bold" color="inherit">
                            {slot.time}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: selectedTimeSlot === slot.time ? 'white' : slot.available ? textSecondary : '#BBB' }}>
                            {slot.available
                              ? (slot.capacity === 1 ? '✅ Disponible' : `✅ ${slot.availableSpots} lugares`)
                              : '❌ Occupado'}
                          </Typography>
                        </Box>
                      </Button>
                    </Grid>
                  ))
                ) : (
                  <Grid size={12}>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color={textPrimary} gutterBottom fontWeight="bold">
                        No hay horarios disponibles
                      </Typography>
                      <Typography variant="body2" color={textSecondary} sx={{ mb: 3 }}>
                        Probá con otra fecha
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleBack}
                        startIcon={<ArrowBack />}
                        sx={{ borderRadius: 3, color: '#FF6B9D', borderColor: '#FF6B9D' }}
                      >
                        Cambiar fecha
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Fade>
        )}

        {bookingStep === 'contact' && (
          <Fade in>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3, bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
              <Typography variant="h6" gutterBottom color={textPrimary}>📱 Tu contacto</Typography>
              <Box sx={{ mx: 0, mt: 0, mb: 3, p: 2.5, bgcolor: summaryBg, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.08)', border: `2px solid ${summaryBorder}` }}>
                <Box sx={{ fontSize: '28px' }}>📋</Box>
                <Box>
                  <Typography variant="body2" color="#FF6B9D" fontWeight="600" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Reserva para:</Typography>
                  <Typography variant="body1" fontWeight="bold" color={textPrimary}>
                    {startDate?.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="#FF6B9D">
                    🕐 a las {selectedTimeSlot} hs
                  </Typography>
                </Box>
              </Box>
              <TextField
                fullWidth
                label="Tu email"
                margin="normal"
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                sx={{ mb: 3 }}
                inputProps={{ style: { fontSize: '1.2rem', padding: '14px', color: textPrimary } }}
                InputLabelProps={{ style: { fontSize: '1.1rem', color: textSecondary } }}
              />

              <Typography variant="body2" color={textPrimary} fontWeight="500" sx={{ mb: 1, mt: 2 }}>
                Teléfono (WhatsApp):
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <FormControl sx={{ minWidth: 90 }}>
                  <Select
                    value={countryPrefix}
                    onChange={e => setCountryPrefix(e.target.value as string)}
                    sx={{ 
                      bgcolor: inputBg,
                      fontSize: '1rem',
                      color: textPrimary,
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: textPrimary
                      }
                    }}
                  >
                    {countryOptions.map(opt => (
                      <MenuItem key={opt.code} value={opt.code} sx={{ bgcolor: cardBg }}>
                        {opt.flag} {opt.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  value={formData.phone}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, phone: val });
                  }}
                  placeholder="11 1234 5678"
                  inputProps={{ 
                    style: { 
                      fontSize: '1.2rem', 
                      padding: '14px',
                      backgroundColor: inputBg,
                      color: textPrimary
                    } 
                  }}
                />
              </Box>
              <Typography variant="caption" color={textSecondary} sx={{ display: 'block', mb: 2 }}>
                Sin 0 ni 15 • Ej: 11 1234 5678
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleBook}
                disabled={loading || !formData.email || !formData.phone || formData.phone.length < 8}
                sx={{
                  mt: 3,
                  py: 2,
                  borderRadius: 3,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #FF6B9D 30%, #FF8E53 90%)',
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : '🎉 Confirmar Reserva'}
              </Button>
            </Paper>
          </Fade>
        )}

        {bookingStep === 'success' && (
          <Fade in>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
              <Box sx={{
                fontSize: '100px',
                mb: 2,
                animation: 'bounce 1s ease-in-out',
                '@keyframes bounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-20px)' }
                }
              }}>
                🎉
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom color={textPrimary}>
                ¡Listo, {userName}! 🎉
              </Typography>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mt: 2, mb: 3, width: '100%', bgcolor: cardBg, border: isDark ? '1px solid #333' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CalendarToday sx={{ color: '#FF6B9D' }} />
                  <Typography variant="body1" color={textPrimary}>
                    {startDate?.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AccessTime sx={{ color: '#FF6B9D' }} />
                  <Typography variant="body1" color={textPrimary}>
                    a las {selectedTimeSlot} hs
                  </Typography>
                </Box>
              </Paper>
              <Typography color={textSecondary} sx={{ mb: 4 }}>
                Te contactaremos a la brevedad para confirmar tu reserva. 📱
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                <LynaAvatar size={56} />
                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, bgcolor: lynaBg, border: isDark ? '1px solid #333' : 'none' }}>
                  <Typography variant="body2" fontWeight="bold" color={textPrimary}>
                    ¡Nos vemos pronto! 😺
                  </Typography>
                </Paper>
              </Box>
              <Button variant="outlined" fullWidth onClick={() => window.location.reload()} sx={{ py: 2, borderRadius: 3, color: '#FF6B9D', borderColor: '#FF6B9D' }}>
                Hacer otra reserva
              </Button>
            </Paper>
          </Fade>
        )}
      </Container>
    </Box>
  );
};

export default PublicReservationWidget;
